import { useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { SensorReading } from '../../api/client';

interface Props {
  readings: SensorReading[];
}

interface ChartPoint {
  time: string;
  drumTemp?: number;
  beansTemp?: number;
  ror?: number;
}

const DRUM_ADDR = '400001';
const BEANS_ADDR = '400003';
const ROR_ADDR = '400005';

function formatTime(iso: string): string {
  try {
    const d = new Date(iso);
    return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}:${d.getSeconds().toString().padStart(2, '0')}`;
  } catch {
    return '';
  }
}

export default function TemperatureChart({ readings }: Props) {
  const data = useMemo<ChartPoint[]>(() => {
    const cutoff = Date.now() - 30 * 60 * 1000;
    const byTime: Record<string, ChartPoint> = {};

    for (const r of readings) {
      if (new Date(r.time).getTime() < cutoff) continue;
      const key = r.time;
      if (!byTime[key]) byTime[key] = { time: formatTime(r.time) };
      const val = parseFloat(r.value);
      if (isNaN(val)) continue;
      if (r.register_address === DRUM_ADDR) byTime[key].drumTemp = Math.round(val * 10) / 10;
      if (r.register_address === BEANS_ADDR) byTime[key].beansTemp = Math.round(val * 10) / 10;
      if (r.register_address === ROR_ADDR) byTime[key].ror = Math.round(val * 10) / 10;
    }

    return Object.values(byTime).sort((a, b) => a.time.localeCompare(b.time));
  }, [readings]);

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400 text-sm">
        ממתין לנתוני חיישנים...
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis dataKey="time" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
        <YAxis yAxisId="temp" domain={['auto', 'auto']} tick={{ fontSize: 10 }} unit="°C" width={45} />
        <YAxis yAxisId="ror" orientation="right" domain={['auto', 'auto']} tick={{ fontSize: 10 }} unit="°/m" width={40} />
        <Tooltip
          formatter={(value: number, name: string) => {
            const labels: Record<string, string> = { drumTemp: 'תנור', beansTemp: 'פולים', ror: 'RoR' };
            return [value, labels[name] ?? name];
          }}
        />
        <Legend formatter={(val: string) => ({ drumTemp: 'טמפ׳ תנור', beansTemp: 'טמפ׳ פולים', ror: 'RoR' }[val] ?? val)} />
        <Line yAxisId="temp" type="monotone" dataKey="drumTemp" stroke="#ef4444" dot={false} strokeWidth={2} name="drumTemp" />
        <Line yAxisId="temp" type="monotone" dataKey="beansTemp" stroke="#f97316" dot={false} strokeWidth={2} name="beansTemp" />
        <Line yAxisId="ror" type="monotone" dataKey="ror" stroke="#6366f1" dot={false} strokeWidth={1.5} strokeDasharray="4 2" name="ror" />
      </LineChart>
    </ResponsiveContainer>
  );
}
